<template>
  <div class="n2n-catalog">
    <PageHeader
      title="카탈로그 관리"
      description="카탈로그 상태를 조회/갱신합니다."
      :margin-bottom="true"
    />

    <section class="toolbar">
      <div class="toolbar-left">
        <div class="filter-item">
          <label class="filter-label">마켓 번호</label>
          <el-select
            v-model="query.marketNumber"
            placeholder="마켓 선택"
            clearable
            filterable
            allow-create
            default-first-option
            class="market-select"
          >
            <el-option
              v-for="market in markets"
              :key="market.marketNumber"
              :label="marketLabel(market)"
              :value="market.marketNumber"
            />
          </el-select>
        </div>
        <div class="filter-item">
          <label class="filter-label">상태</label>
          <el-select v-model="query.catalog" placeholder="선택" class="catalog-select">
            <el-option v-for="status in queryCatalogStatuses" :key="status" :label="status" :value="status" />
          </el-select>
        </div>
        <div class="toolbar-actions">
          <el-button type="primary" :loading="loading" @click="handleFetch">조회</el-button>
          <el-button plain :disabled="!items.length" @click="copyItems">리스트 복사</el-button>
        </div>
      </div>

      <div class="toolbar-right">
        <el-button  :disabled="loading" @click="openUpdateModal">상태 업데이트</el-button>
      </div>
    </section>

    <section class="content">
      <AppLoading v-if="loading" text="카탈로그 정보를 불러오는 중입니다..." />
      <template v-else>
        <div class="summary-card">
          <div class="summary-grid">
            <div v-for="status in queryCatalogStatuses" :key="status" class="summary-item">
              <div class="summary-label">{{ status }}</div>
              <div class="summary-count">{{ formatNumber(counts.uploadSuccessCatalog[status] ?? 0) }}</div>
            </div>
          </div>
        </div>

        <div class="list-card">
          <div class="list-header">
            <div class="list-title">상품번호</div>
            <div class="list-sub">
              {{ items.length ? `${formatNumber(items.length)}개` : '0개' }}
            </div>
          </div>

          <div class="result-content">
            <el-input
              v-model="itemsText"
              type="textarea"
              :rows="15"
              readonly
              placeholder="조회된 상품번호가 여기에 표시됩니다."
              class="result-textarea"
            />
          </div>
        </div>
      </template>
    </section>

    <el-dialog
      v-model="updateModal.visible"
      title="카탈로그 상태 업데이트"
      width="720px"
      destroy-on-close
      :close-on-click-modal="false"
      align-center
    >
      <div class="modal-body">
        <div class="modal-field">
          <label class="filter-label">변경할 상태</label>
          <div class="status-choice-row">
            <el-button
              v-for="status in updateCatalogStatuses"
              :key="status"
              plain
              class="status-choice-item"
              :class="{ 'is-active': updateModal.catalog === status }"
              @click="selectUpdateStatus(status)"
            >
              {{ status }}
            </el-button>
          </div>
        </div>

        <div class="modal-field">
          <label class="filter-label">상품번호 (한 줄에 1개)</label>
          <el-input
            v-model="updateModal.text"
            type="textarea"
            :rows="14"
            placeholder="예)
1234567890
1234567891"
          />
          <p class="modal-hint">
            * 역방향 변경 불가:
            <b>카탈로그매칭됨 → 카탈로그최적화 → 카탈로그최적화제외</b>
          </p>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button :disabled="updateModal.loading" @click="closeUpdateModal">닫기</el-button>
          <el-button
            type="primary"
            :loading="updateModal.loading"
            @click="submitUpdate"
          >
            업데이트 실행
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import PageHeader from '@/components/common/header.vue';
import AppLoading from '@/components/common/loading.vue';
import { fetchCatalog, updateCatalog } from '@/services/n2n';
import { getReRegisterMarkets } from '@/services/manager';

export default {
  name: 'N2nCatalog',
  components: {
    PageHeader,
    AppLoading
  },
  setup() {
    const queryCatalogStatuses = [
      '매칭안됨',
      '카탈로그매칭됨',
      '카탈로그최적화',
      '카탈로그최적화제외',
      '판매됨'
    ];

    const updateCatalogStatuses = [
      '매칭안됨',
      '카탈로그매칭됨',
      '카탈로그최적화',
      '카탈로그최적화제외'
    ];

    const loading = ref(false);
    const items = ref([]);
    const markets = ref([]);
    const counts = reactive({
      uploadSuccessTotal: 0,
      uploadSuccessCatalog: {}
    });

    const query = reactive({
      catalog: '매칭안됨',
      marketNumber: null
    });

    const updateModal = reactive({
      visible: false,
      loading: false,
      catalog: '카탈로그최적화',
      text: '',
    });

    const marketLabel = market => {
      if (!market) return '-';
      const memo = market.marketMemo ? ` - ${market.marketMemo}` : '';
      return `${market.marketNumber}${memo}`;
    };

    const formatNumber = value => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value ?? '-';
      return num.toLocaleString();
    };

    const normalizeMarketNumber = value => {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      if (!Number.isInteger(num) || num <= 0) return null;
      return num;
    };

    const parseProductNos = text => {
      const rows = String(text || '')
        .split(/\r?\n/)
        .map(v => v.trim())
        .filter(Boolean);

      const unique = [];
      const seen = new Set();
      for (const no of rows) {
        if (!/^\d+$/.test(no)) continue;
        if (seen.has(no)) continue;
        seen.add(no);
        unique.push(no);
      }
      return unique;
    };

    const itemsText = computed(() => (items.value || []).join('\n'));

    const handleFetch = async () => {
      const marketNumber = normalizeMarketNumber(query.marketNumber);
      if (!marketNumber) {
        ElMessage.warning('market_number(마켓 번호)를 올바르게 입력하세요.');
        return;
      }
      if (!queryCatalogStatuses.includes(query.catalog)) {
        ElMessage.warning('catalog 상태를 선택하세요.');
        return;
      }

      loading.value = true;
      try {
        const params = {
          catalog: query.catalog,
          market_number: marketNumber
        };
        const { data } = await fetchCatalog(params);
        const payload = data?.data ?? data;
        items.value = payload?.items ?? [];
        counts.uploadSuccessTotal = payload?.counts?.uploadSuccessTotal ?? 0;
        counts.uploadSuccessCatalog = payload?.counts?.uploadSuccessCatalog ?? {};
      } catch (error) {
        items.value = [];
        counts.uploadSuccessTotal = 0;
        counts.uploadSuccessCatalog = {};
        ElMessage.error(error.response?.data?.message || '카탈로그 정보를 불러오지 못했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const copyItems = async () => {
      if (!items.value.length) return;
      try {
        await navigator.clipboard.writeText(itemsText.value);
        ElMessage.success('복사되었습니다.');
      } catch {
        ElMessage.error('복사에 실패했습니다.');
      }
    };

    const openUpdateModal = () => {
      updateModal.visible = true;
    };

    const selectUpdateStatus = status => {
      updateModal.catalog = status;
    };

    const closeUpdateModal = () => {
      updateModal.visible = false;
      updateModal.loading = false;
      updateModal.text = '';
    };

    const submitUpdate = async () => {
      if (updateModal.loading) return;
      if (!updateCatalogStatuses.includes(updateModal.catalog)) {
        ElMessage.warning('변경할 catalog 상태를 선택하세요.');
        return;
      }
      const smartstoreChannelProductNos = parseProductNos(updateModal.text);
      if (!smartstoreChannelProductNos.length) {
        ElMessage.warning('상품번호를 한 줄에 1개씩 입력하세요.');
        return;
      }

      const payload = {
        smartstoreChannelProductNos,
        catalog: updateModal.catalog
      };

      updateModal.loading = true;
      try {
        await updateCatalog(payload);
        ElMessage.success('업데이트 완료');
        closeUpdateModal();
        const currentMarket = normalizeMarketNumber(query.marketNumber);
        if (currentMarket) {
          handleFetch();
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '업데이트에 실패했습니다.');
      } finally {
        updateModal.loading = false;
      }
    };

    const loadMarkets = async () => {
      try {
        const response = await getReRegisterMarkets('naver');
        if (response?.success) {
          markets.value = response.data || [];
          if (!query.marketNumber && markets.value.length) {
            query.marketNumber = markets.value[0].marketNumber;
          }
          return;
        }
        markets.value = [];
        ElMessage.error(response?.message || '마켓 목록을 불러오지 못했습니다.');
      } catch (error) {
        markets.value = [];
        ElMessage.error(error.response?.data?.message || '마켓 목록 조회 중 오류가 발생했습니다.');
      }
    };

    onMounted(() => {
      loadMarkets();
    });

    return {
      queryCatalogStatuses,
      updateCatalogStatuses,
      loading,
      items,
      markets,
      counts,
      query,
      updateModal,
      itemsText,
      marketLabel,
      formatNumber,
      handleFetch,
      copyItems,
      openUpdateModal,
      selectUpdateStatus,
      closeUpdateModal,
      submitUpdate
    };
  }
};
</script>

<style scoped>
.n2n-catalog {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--el-bg-color-page);
  overflow-y: auto;
  box-sizing: border-box;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm);
  flex-wrap: nowrap;
  margin-bottom: var(--spacing-xs);
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  min-width: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 220px;
}

.filter-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.catalog-select {
  min-width: 200px;
}

.market-select {
  min-width: 240px;
}

.toolbar-actions {
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
  flex-shrink: 0;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  min-height: 0;
  overflow: hidden;
}

.summary-card,
.list-card {
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  border: 1px solid var(--el-border-color-lighter);
  padding: var(--spacing-sm);
}

.list-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.list-card {
  overflow: hidden;
}

.summary-title {
  font-weight: var(--el-font-weight-semibold);
  color: var(--el-text-color-primary);
  margin-bottom: var(--spacing-sm);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--spacing-xs);
}

.summary-item {
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: 10px;
  background: var(--el-fill-color-lighter);
}

.summary-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.summary-count {
  margin-top: 4px;
  font-weight: var(--el-font-weight-bold);
  color: var(--el-text-color-primary);
  font-size: var(--el-font-size-medium);
}

.summary-hint {
  margin: var(--spacing-sm) 0 0;
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.list-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm);
}

.list-title {
  font-weight: var(--el-font-weight-semibold);
  color: var(--el-text-color-primary);
}

.list-sub {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.status-choice-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
}

.status-choice-item {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
}

.status-choice-row :deep(.el-button + .el-button) {
  margin-left: 0;
}

.status-choice-item.is-active {
  --el-button-bg-color: var(--el-color-primary-light-9);
  --el-button-border-color: var(--el-color-primary);
  --el-button-text-color: var(--el-color-primary);
}

.modal-hint {
  margin: 0;
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xs);
}

.result-content {
  flex: 1;
  overflow: auto;
  position: relative;
}

.result-textarea {
  height: 100%;
  font-family: monospace;
}

:deep(.el-textarea__inner) {
  height: 100% !important;
  resize: none;
}

@media (max-width: 768px) {
  .toolbar {
    flex-wrap: wrap;
  }

  .toolbar-left {
    flex-wrap: wrap;
    width: 100%;
  }

  .toolbar-right {
    width: 100%;
    justify-content: flex-end;
  }

  .filter-item {
    width: 100%;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
  }

  .catalog-select {
    width: 100%;
  }

  .status-choice-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}
</style>

